import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
doc, getDoc, collection, query, where, getDocs, 
deleteDoc, writeBatch, updateDoc 
} from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
faArrowLeft, faEdit, faTrash, 
faFilePdf, faFileAlt, faSpinner,
faExclamationTriangle, faTools, faCalendarAlt, faMapMarkerAlt,
faQrcode, faCheck, faPrint, faUserPlus, faUserLock, faTimes, faUser, faPlus
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import FileViewerModal from '../common/FileViewerModal';
import ConfirmModal from '../common/ConfirmModal';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import QRCode from "react-qr-code";
import styles from './EquipmentDetails.module.css';
import { sendInventoryUpdateNotification } from '../../utils/NotificationService';

const EquipmentCardDetails = () => {
const { category, itemId } = useParams();
const navigate = useNavigate();
const { userData } = useContext(UserContext);

// State for data structure
const [groupData, setGroupData] = useState(null);
const [individualItems, setIndividualItems] = useState([]);
const [activeTab, setActiveTab] = useState('details'); // 'details' or itemId

const [isLoading, setIsLoading] = useState(true);
const [isLoadingItems, setIsLoadingItems] = useState(false);
const [showFileViewer, setShowFileViewer] = useState(false);
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [error, setError] = useState(null);
const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
const [tours, setTours] = useState([]);

// Status modal state
const [showStatusModal, setShowStatusModal] = useState(false);
const [selectedItem, setSelectedItem] = useState(null);
const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

// QR code modal state
const [showQrModal, setShowQrModal] = useState(false);
const [selectedQrItem, setSelectedQrItem] = useState(null);

// Access control modal state
const [showAccessModal, setShowAccessModal] = useState(false);
const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);
const [newUserEmail, setNewUserEmail] = useState('');

// Staff assignment state
const [showStaffModal, setShowStaffModal] = useState(false);
const [availableStaff, setAvailableStaff] = useState([]);
const [selectedStaffIds, setSelectedStaffIds] = useState([]);
const [isLoadingStaff, setIsLoadingStaff] = useState(false);
const [isUpdatingStaff, setIsUpdatingStaff] = useState(false);
const [selectedTourId, setSelectedTourId] = useState(null);

// File viewer state
const [fileViewerUrl, setFileViewerUrl] = useState('');
const [fileViewerType, setFileViewerType] = useState('');
const [fileViewerName, setFileViewerName] = useState('');

const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
const [showRemoveAssignmentConfirm, setShowRemoveAssignmentConfirm] = useState(false);
const [itemToUnassign, setItemToUnassign] = useState(null);
const [tourIdToUnassign, setTourIdToUnassign] = useState(null);
const [isUnassigning, setIsUnassigning] = useState(false);
const [dateIdToUnassign, setDateIdToUnassign] = useState(null);

const [showRemoveDateAssignmentConfirm, setShowRemoveDateAssignmentConfirm] = useState(false);

const [staffPermissions, setStaffPermissions] = useState({
  canAddItems: true,
  canEditItems: true,
  canDeleteItems: true,
  canChangeStatus: true
});

// App URL for QR codes
const APP_URL = 'https://ontour.co.in';

// Check for screen size changes
useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };
  
  window.addEventListener('resize', handleResize);
  return () => {
    window.removeEventListener('resize', handleResize);
  };
}, []);

useEffect(() => {
  if (userData) {
    // Check if the user is staff and set permissions
    setStaffPermissions(getStaffPermissions());
  }
}, [userData]);

// Get the formatted display name for the category
const getCategoryDisplayName = (categoryId) => {
  const categories = {
    'audioEquipment': 'Audio Equipment',
    'backlineEquipment': 'Backline Equipment',
    'lightingEquipment': 'Lighting Equipment',
    'visualEquipment': 'Visual Equipment',
    'pyroEquipment': 'Pyro Equipment',
    'stagingEquipment': 'Staging Equipment',
    'miscellaneousEquipment': 'Miscellaneous Equipment'
  };
  return categories[categoryId] || 'Equipment';
};

// Fetch the item details
useEffect(() => {
  const fetchItemDetails = async () => {
    if (!userData?.userId || !category || !itemId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const db = getFirestoreInstance();
      
      // Get from inventoryGroups collection
      const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', itemId);
      let groupSnap = await getDoc(groupRef);
      
      if (groupSnap.exists()) {
        // New structure - set group data
        const groupData = {
          id: groupSnap.id,
          ...groupSnap.data()
        };
        
        setGroupData(groupData);
        
        // Also fetch individual items in this group
        await fetchIndividualItems(groupData.groupId);
      } else {
        setError('Equipment item not found');
      }
    } catch (error) {
      console.error('Error fetching equipment details:', error);
      setError('Failed to load equipment details');
    } finally {
      setIsLoading(false);
    }
  };
  
  fetchItemDetails();
}, [userData, category, itemId]);

// Debug function to inspect data structure
const logEventData = (dateInfo) => {
  console.log('Date Info Structure:', JSON.stringify(dateInfo, null, 2));
  if (dateInfo.event) {
    console.log('Event exists:', dateInfo.event);
    if (dateInfo.event.venueDetails) {
      console.log('Venue details exist:', dateInfo.event.venueDetails);
      if (dateInfo.event.venueDetails.venueData) {
        console.log('Venue data exists:', dateInfo.event.venueDetails.venueData);
      } else {
        console.log('No venueData in venueDetails');
      }
    } else {
      console.log('No venueDetails in event');
    }
  } else {
    console.log('No event in dateInfo');
  }
};

// Fetch additional tour date information for assigned dates
useEffect(() => {
  if (!userData?.userId || activeTab === 'details' || !individualItems.length) return;
  
  const selectedItem = individualItems.find(item => item.id === activeTab);
  if (!selectedItem || !selectedItem.assignedTourIds) return;
  
  const fetchTourDateDetails = async () => {
    const db = getFirestoreInstance();
    const updatedIndividualItems = [...individualItems];
    let itemUpdated = false;
    
    // For each tour
    for (const [tourId, tourDetails] of Object.entries(selectedItem.assignedTourIds)) {
      if (!tourDetails.assignedDates || !tourDetails.assignedDates.length) continue;
      
      // For each date in the tour
      for (let i = 0; i < tourDetails.assignedDates.length; i++) {
        const dateInfo = tourDetails.assignedDates[i];
        if (!dateInfo.tourDateDocId || dateInfo.event?.venueDetails?.venueData) continue;
        
        try {
          // Fetch the tour date document to get event details
          const tourDateRef = doc(db, 'Tours', tourId, 'tourDates', dateInfo.tourDateDocId);
          const tourDateSnap = await getDoc(tourDateRef);
          
          if (tourDateSnap.exists()) {
            const tourDateData = tourDateSnap.data();
            
            // Update the date info with event data
            if (tourDateData.event) {
              // Find the item in our local state
              const itemIndex = updatedIndividualItems.findIndex(item => item.id === selectedItem.id);
              if (itemIndex !== -1) {
                // Update the specific date with event information
                updatedIndividualItems[itemIndex].assignedTourIds[tourId].assignedDates[i].event = tourDateData.event;
                itemUpdated = true;
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching tour date details for ${tourId}/${dateInfo.tourDateDocId}:`, error);
        }
      }
    }
    
    // Update state if any items were updated
    if (itemUpdated) {
      setIndividualItems(updatedIndividualItems);
    }
  };
  
  fetchTourDateDetails();
}, [userData, activeTab, individualItems]);

// Fetch individual items for a group
const fetchIndividualItems = async (groupId) => {
  if (!groupId || !userData?.userId) return;
  
  setIsLoadingItems(true);
  
  try {
    const db = getFirestoreInstance();
    const itemsQuery = query(
      collection(db, 'users', userData.userId, 'inventoryItems'),
      where('groupId', '==', groupId)
    );
    
    const itemsSnap = await getDocs(itemsQuery);
    
    if (!itemsSnap.empty) {
      const items = itemsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setIndividualItems(items);
      
      // If this is the first load, set the active tab to the first item
      if (items.length > 0 && activeTab === 'details') {
        setActiveTab('details'); // Keep the main details tab active by default
      }
    } else {
      setIndividualItems([]);
    }
  } catch (error) {
    console.error('Error fetching individual items:', error);
  } finally {
    setIsLoadingItems(false);
  }
};

 // This function performs the actual removal after confirmation
 const handleRemoveDateAssignment = async () => {
  if (!itemToUnassign || !tourIdToUnassign || !dateIdToUnassign || !userData?.userId) return;
  
  setIsUnassigning(true);
  
  try {
    const db = getFirestoreInstance();
    const batch = writeBatch(db);
    
    // Get the assignment details
    const assignmentDetails = itemToUnassign.assignedTourIds[tourIdToUnassign];
    if (!assignmentDetails || !assignmentDetails.assignedDates) {
      throw new Error('Assignment details not found');
    }
    
    // Check if this is the only date for this tour
    const isLastDate = assignmentDetails.assignedDates.length === 1;
    
    // Step 1: Update the individual item's assignedTourIds
    const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', itemToUnassign.id);
    
    if (isLastDate) {
      // If it's the last date, remove the entire tour assignment
      const updatedAssignedTourIds = { ...itemToUnassign.assignedTourIds };
      delete updatedAssignedTourIds[tourIdToUnassign];
      
      // Update status if no tours are left
      const newStatus = Object.keys(updatedAssignedTourIds).length === 0 ? 'Available' : 'Reserved';
      
      batch.update(itemRef, {
        assignedTourIds: updatedAssignedTourIds,
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
    } else {
      // If other dates remain, just remove this specific date
      const updatedDates = assignmentDetails.assignedDates.filter(
        date => date.tourDateDocId !== dateIdToUnassign
      );
      
      batch.update(itemRef, {
        [`assignedTourIds.${tourIdToUnassign}.assignedDates`]: updatedDates,
        updatedAt: new Date().toISOString()
      });
    }
    
    // Step 2: Remove from the tour date's assignedInventoryItems collection
    const assignedItemRef = doc(
      db, 
      'Tours', 
      tourIdToUnassign, 
      'tourDates', 
      dateIdToUnassign, 
      'assignedInventoryItems', 
      itemToUnassign.id
    );
    
    batch.delete(assignedItemRef);
    
    // Check if this is the last item from this group for this date
    const groupItemsQuery = query(
      collection(db, 'Tours', tourIdToUnassign, 'tourDates', dateIdToUnassign, 'assignedInventoryItems'),
      where('groupId', '==', itemToUnassign.groupId)
    );
    
    const groupItemsSnapshot = await getDocs(groupItemsQuery);
    
    // If this is the only/last item from this group for this date, remove the group entry
    if (groupItemsSnapshot.size <= 1) {
      const assignedGroupRef = doc(
        db,
        'Tours',
        tourIdToUnassign,
        'tourDates',
        dateIdToUnassign,
        'assignedInventoryGroups',
        itemToUnassign.groupId
      );
      
      batch.delete(assignedGroupRef);
    }
    
    // Step 3: Update the inventory group's counts and assignedTourIds
    const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', itemToUnassign.groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (groupDoc.exists()) {
      const groupData = groupDoc.data();
      
      // Update the counts - increase available, decrease reserved
      const newAvailable = (groupData.availableQuantity || 0) + 1;
      const newReserved = Math.max(0, (groupData.reservedQuantity || 0) - 1);
      
      // Update assignedTourIds in the group
      let updatedGroupAssignedTourIds = { ...groupData.assignedTourIds || {} };
      
      if (updatedGroupAssignedTourIds[tourIdToUnassign]) {
        if (isLastDate) {
          // If it's the last date, remove the entire tour
          delete updatedGroupAssignedTourIds[tourIdToUnassign];
        } else {
          // Otherwise, remove just this specific date
          const updatedGroupDates = updatedGroupAssignedTourIds[tourIdToUnassign].assignedDates.filter(
            date => date.tourDateDocId !== dateIdToUnassign
          );
          
          updatedGroupAssignedTourIds[tourIdToUnassign].assignedDates = updatedGroupDates;
          
          // Also remove this item ID from assignedItems if it exists
          if (updatedGroupAssignedTourIds[tourIdToUnassign].assignedItems) {
            const assignedItems = updatedGroupAssignedTourIds[tourIdToUnassign].assignedItems;
            // Check if there are other dates that still use this item
            const stillHasOtherDates = assignmentDetails.assignedDates.some(
              date => date.tourDateDocId !== dateIdToUnassign
            );
            
            if (!stillHasOtherDates) {
              // If no other dates use this item, remove it from assignedItems
              updatedGroupAssignedTourIds[tourIdToUnassign].assignedItems = 
                assignedItems.filter(id => id !== itemToUnassign.id);
              
              // If no more items are assigned, remove the tour entirely
              if (updatedGroupAssignedTourIds[tourIdToUnassign].assignedItems.length === 0) {
                delete updatedGroupAssignedTourIds[tourIdToUnassign];
              }
            }
          }
        }
      }
      
      batch.update(groupRef, {
        availableQuantity: newAvailable,
        reservedQuantity: newReserved,
        assignedTourIds: updatedGroupAssignedTourIds,
        updatedAt: new Date().toISOString()
      });
    }
    
    // Commit all the changes
    await batch.commit();
    
    // Update local state
    const updatedItems = individualItems.map(item => {
      if (item.id === itemToUnassign.id) {
        const updatedItem = { ...item };
        const assignmentDetails = updatedItem.assignedTourIds[tourIdToUnassign];
        
        if (isLastDate) {
          // Remove entire tour if this was the last date
          updatedItem.assignedTourIds = { ...updatedItem.assignedTourIds };
          delete updatedItem.assignedTourIds[tourIdToUnassign];
          
          // Update status if no more assignments
          if (Object.keys(updatedItem.assignedTourIds).length === 0) {
            updatedItem.status = 'Available';
          }
        } else {
          // Just remove this specific date
          updatedItem.assignedTourIds = { 
            ...updatedItem.assignedTourIds,
            [tourIdToUnassign]: {
              ...assignmentDetails,
              assignedDates: assignmentDetails.assignedDates.filter(
                date => date.tourDateDocId !== dateIdToUnassign
              )
            }
          };
        }
        
        return updatedItem;
      }
      return item;
    });
    
    setIndividualItems(updatedItems);
    
    // Update the group data in local state
    if (groupData) {
      setGroupData({
        ...groupData,
        availableQuantity: (groupData.availableQuantity || 0) + 1,
        reservedQuantity: Math.max(0, (groupData.reservedQuantity || 0) - 1)
      });
    }
    
    alert('Item successfully unassigned from this date.');
    
  } catch (error) {
    console.error('Error removing assignment:', error);
    alert(`Error removing assignment: ${error.message}`);
  } finally {
    setIsUnassigning(false);
    setShowRemoveDateAssignmentConfirm(false);
    setItemToUnassign(null);
    setTourIdToUnassign(null);
    setDateIdToUnassign(null);
  }
};

// Handle edit button click
const handleEdit = () => {
  if (!isActionAllowed('edit')) {
    alert('You do not have permission to edit inventory items.');
    return;
  }
  navigate(`/inventory?edit=${category}_${groupData?.id}`);
};

// Update handleDeleteConfirm function to check permissions
const handleDeleteConfirm = () => {
  if (!isActionAllowed('delete')) {
    alert('You do not have permission to delete inventory items.');
    return;
  }
  setShowDeleteConfirm(true);
};

// Handle back button click
const handleBack = () => {
  navigate('/inventory');
};

// Handle actual deletion
// Handle actual deletion
const handleDelete = async () => {
  try {
    const db = getFirestoreInstance();
    
    if (groupData) {
      // First check for assigned tour dates in any of the group's items
      const itemsQuery = query(
        collection(db, 'users', userData.userId, 'inventoryItems'),
        where('groupId', '==', groupData.groupId)
      );
      
      const itemsSnap = await getDocs(itemsQuery);
      
      // Collect all assigned tour information from individual items
      const assignedTourRefs = new Map(); // Map of tourId -> Set of dateIds
      
      for (const itemDoc of itemsSnap.docs) {
        const itemData = itemDoc.data();
        if (itemData.assignedTourIds && Object.keys(itemData.assignedTourIds).length > 0) {
          // Process each assigned tour
          for (const [tourId, tourDetails] of Object.entries(itemData.assignedTourIds)) {
            if (!tourDetails.assignedDates || tourDetails.assignedDates.length === 0) {
              continue;
            }
            
            // Add each tour date to our map
            if (!assignedTourRefs.has(tourId)) {
              assignedTourRefs.set(tourId, new Set());
            }
            
            for (const dateInfo of tourDetails.assignedDates) {
              if (dateInfo.tourDateDocId) {
                assignedTourRefs.get(tourId).add(dateInfo.tourDateDocId);
              }
            }
          }
        }
      }
      
      // Clean up assigned inventory in tour dates if any exist
      if (assignedTourRefs.size > 0) {
        console.log(`Cleaning up assigned inventory references in ${assignedTourRefs.size} tours`);
        
        // Process each tour and its dates
        for (const [tourId, dateIds] of assignedTourRefs.entries()) {
          for (const dateId of dateIds) {
            // 1. Delete all items in this group from assignedInventoryItems subcollection
            const assignedItemsRef = collection(
              db, 
              'Tours', 
              tourId, 
              'tourDates', 
              dateId, 
              'assignedInventoryItems'
            );
            
            // Get all items from this group in the subcollection
            const itemsQuery = query(
              assignedItemsRef,
              where('groupId', '==', groupData.groupId)
            );
            
            const itemsSnapshot = await getDocs(itemsQuery);
            
            // Delete each item document
            if (!itemsSnapshot.empty) {
              const batch = writeBatch(db);
              
              itemsSnapshot.forEach(doc => {
                batch.delete(doc.ref);
              });
              
              await batch.commit();
              console.log(`Deleted ${itemsSnapshot.size} items from assignedInventoryItems subcollection`);
            }
            
            // 2. Delete the group document from assignedInventoryGroups subcollection
            const groupDocRef = doc(
              db,
              'Tours',
              tourId,
              'tourDates',
              dateId,
              'assignedInventoryGroups',
              groupData.groupId
            );
            
            // Check if the group document exists
            const groupDoc = await getDoc(groupDocRef);
            
            if (groupDoc.exists()) {
              await deleteDoc(groupDocRef);
              console.log(`Deleted group document from assignedInventoryGroups subcollection`);
            }
            
            // 3. Clean up old field-based data if it exists (for backward compatibility)
            const tourDateRef = doc(db, 'Tours', tourId, 'tourDates', dateId);
            const tourDateDoc = await getDoc(tourDateRef);
            
            if (!tourDateDoc.exists()) continue;
            
            const tourDateData = tourDateDoc.data();
            let updatedData = {};
            
            // Handle old assignedInventoryItems field
            if (tourDateData.assignedInventoryItems) {
              const assignedItems = { ...tourDateData.assignedInventoryItems };
              let itemsRemoved = false;
              
              // Remove any items that belong to this group
              for (const [itemId, itemData] of Object.entries(assignedItems)) {
                if (itemData.groupId === groupData.groupId) {
                  delete assignedItems[itemId];
                  itemsRemoved = true;
                }
              }
              
              if (itemsRemoved) {
                updatedData.assignedInventoryItems = assignedItems;
              }
            }
            
            // Handle old assignedInventoryGroups field
            if (tourDateData.assignedInventoryGroups && 
                tourDateData.assignedInventoryGroups[groupData.groupId]) {
              
              const assignedGroups = { ...tourDateData.assignedInventoryGroups };
              delete assignedGroups[groupData.groupId];
              updatedData.assignedInventoryGroups = assignedGroups;
            }
            
            // Update the tour date document if changes were made to old fields
            if (Object.keys(updatedData).length > 0) {
              await updateDoc(tourDateRef, updatedData);
              console.log(`Cleaned up old field data in tour ${tourId}, date ${dateId}`);
            }
          }
        }
      }
      
      // Now proceed with deleting the group document
      const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', groupData.groupId);
      await deleteDoc(groupRef);
      
      // Delete all individual items
      const batch = writeBatch(db);
      
      itemsSnap.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      
      await batch.commit();
      
      // Navigate back to inventory
      navigate('/inventory');
    } else {
      setError('Failed to delete item - no group data found');
    }
  } catch (error) {
    console.error('Error deleting item:', error);
    setError('Failed to delete item');
  }
};

// Handle tour selection (for RightMenu)
const handleTourSelect = (tour) => {
  // Navigate to tour or update state as needed
};

// Handle opening the status modal
const handleStatusClick = (item) => {
  if (!isActionAllowed('status')) {
    alert('You do not have permission to change item status.');
    return;
  }
  setSelectedItem(item);
  setShowStatusModal(true);
};

// Handle QR code button click
const handleQrCodeClick = (item) => {
  setSelectedQrItem(item);
  setShowQrModal(true);
};

// Handle access control button click
const handleAccessControlClick = (item) => {
  setSelectedItem(item);
  setShowAccessModal(true);
  setNewUserEmail('');
};

// Helper function to check staff permissions
const getStaffPermissions = () => {
  // If not staff, assume full permissions
  if (!userData || userData.userType !== 'Staff') {
    return {
      canAddItems: true,
      canEditItems: true,
      canDeleteItems: true,
      canChangeStatus: true
    };
  }
  
  // Check for organization data in userData
  const vendorOrganization = userData.organizations?.find(org => 
    org.organizationId === userData.currentOrganizationId
  );
  
  // Return the permissions from organization data or default to no permissions
  return vendorOrganization?.permissions || {
    canAddItems: false,
    canEditItems: false,
    canDeleteItems: false,
    canChangeStatus: false
  };
};

const isActionAllowed = (action) => {
  if (!userData || userData.userType !== 'Staff') {
    return true; // Non-staff users have full permissions
  }
  
  switch (action) {
    case 'add':
      return staffPermissions.canAddItems;
    case 'edit':
      return staffPermissions.canEditItems;
    case 'delete':
      return staffPermissions.canDeleteItems;
    case 'status':
      return staffPermissions.canChangeStatus;
    default:
      return false;
  }
};

// Remove a user from allowed users
const handleRemoveAllowedUser = async (userId) => {
  if (!selectedItem || !userId || !userData?.userId) return;
  
  setIsUpdatingAccess(true);
  
  try {
    const db = getFirestoreInstance();
    
    // Update item document to remove user from allowed list
    const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', selectedItem.id);
    const updatedAllowedUserIds = selectedItem.allowedUserIds.filter(id => id !== userId);
    
    await updateDoc(itemRef, {
      allowedUserIds: updatedAllowedUserIds,
      updatedAt: new Date().toISOString()
    });
    
    // Update local state
    const updatedItems = individualItems.map(item => 
      item.id === selectedItem.id ? {
        ...item,
        allowedUserIds: item.allowedUserIds.filter(id => id !== userId)
      } : item
    );
    
    setIndividualItems(updatedItems);
    alert('User access removed successfully.');
  } catch (error) {
    console.error('Error updating access:', error);
    alert('Failed to update access: ' + error.message);
  } finally {
    setIsUpdatingAccess(false);
  }
};

// Handle updating the item status
const handleUpdateStatus = async (newStatus) => {
  if (!selectedItem || !userData?.userId) return;
  
  setIsUpdatingStatus(true);
  
  try {
    const db = getFirestoreInstance();
    
    // Store the previous status before updating
    const oldStatus = selectedItem.status;
    
    // Update the individual item
    const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', selectedItem.id);
    await updateDoc(itemRef, {
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
    
    // Update the group's status counts
    const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', groupData.id);
    
    // Get current group data to calculate new counts
    const groupSnap = await getDoc(groupRef);
    if (groupSnap.exists()) {
      const currentGroup = groupSnap.data();
      
      // Calculate the count changes
      const updatedGroup = { ...currentGroup };
      
      // Decrease count of old status
      if (oldStatus === 'Available') {
        updatedGroup.availableQuantity = Math.max(0, (updatedGroup.availableQuantity || 0) - 1);
      } else if (oldStatus === 'Damaged') {
        updatedGroup.damagedQuantity = Math.max(0, (updatedGroup.damagedQuantity || 0) - 1);
      } else if (oldStatus === 'Maintenance') {
        updatedGroup.maintenanceQuantity = Math.max(0, (updatedGroup.maintenanceQuantity || 0) - 1);
      }
      
      // Increase count of new status
      if (newStatus === 'Available') {
        updatedGroup.availableQuantity = (updatedGroup.availableQuantity || 0) + 1;
      } else if (newStatus === 'Damaged') {
        updatedGroup.damagedQuantity = (updatedGroup.damagedQuantity || 0) + 1;
      } else if (newStatus === 'Maintenance') {
        updatedGroup.maintenanceQuantity = (updatedGroup.maintenanceQuantity || 0) + 1;
      }
      
      // Update group document
      await updateDoc(groupRef, {
        availableQuantity: updatedGroup.availableQuantity,
        damagedQuantity: updatedGroup.damagedQuantity,
        maintenanceQuantity: updatedGroup.maintenanceQuantity,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setGroupData({
        ...groupData,
        availableQuantity: updatedGroup.availableQuantity,
        damagedQuantity: updatedGroup.damagedQuantity,
        maintenanceQuantity: updatedGroup.maintenanceQuantity
      });
      
      // Update the item in the local state
      const updatedItems = individualItems.map(item => 
        item.id === selectedItem.id ? { ...item, status: newStatus } : item
      );
      setIndividualItems(updatedItems);
    }
    
    // Send notification if user is staff
    if (userData.userType === 'Staff' && userData.currentOrganizationId) {
      try {
        await sendInventoryUpdateNotification(
          userData.currentOrganizationId,
          {
            userId: userData.userId,
            fullName: userData.fullName || 'Staff member',
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl
          },
          'status_change',
          {
            id: selectedItem.id,
            name: selectedItem.name || groupData.name,
            type: getCategoryDisplayName(category),
            category: category,
            status: newStatus,
            oldStatus: oldStatus // Include the old status for context
          }
        );
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }
    
    setShowStatusModal(false);
  } catch (error) {
    console.error('Error updating status:', error);
  } finally {
    setIsUpdatingStatus(false);
  }
};

// Handle opening the staff modal
const handleManageStaffClick = (tourId) => {
  setSelectedTourId(tourId);
  fetchAvailableStaff();
  setShowStaffModal(true);
};

// Fetch available staff members
const fetchAvailableStaff = async () => {
  if (!userData?.userId) return;
  
  setIsLoadingStaff(true);
  
  try {
    const db = getFirestoreInstance();
    
    // Fetch staff from subcollection
    const staffQuery = query(
      collection(db, 'users', userData.userId, 'staff'),
      where('status', '==', 'accepted')
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    
    if (staffSnapshot.empty) {
      setAvailableStaff([]);
      return;
    }
    
    const staffData = await Promise.all(
      staffSnapshot.docs.map(async (docSnapshot) => {
        const staffData = docSnapshot.data();
        
        try {
          const userDoc = await getDoc(doc(db, 'users', docSnapshot.id));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              id: docSnapshot.id,
              fullName: userData.fullName || '',
              userName: userData.userName || '',
              profilePictureUrl: userData.profilePictureUrl || '',
              userSubType: userData.userSubType || '',
              email: userData.email || '',
              role: staffData.role || 'staff'
            };
          }
          return null;
        } catch (err) {
          console.error(`Error fetching staff data for ${docSnapshot.id}:`, err);
          return null;
        }
      })
    );
    
    // Get already assigned staff for this tour
    const selectedItem = individualItems.find(item => item.id === activeTab);
    const assignedStaffIds = selectedItem?.assignedTourIds?.[selectedTourId]?.assignedStaff || [];
    
    // Filter out already assigned staff
    const filteredStaff = staffData.filter(Boolean);
    
    setAvailableStaff(filteredStaff);
  } catch (error) {
    console.error('Error fetching staff data:', error);
  } finally {
    setIsLoadingStaff(false);
  }
};

// Remove assigned staff from item
const handleRemoveStaff = async (tourId, staffId) => {
  if (!selectedItem || !userData?.userId || !tourId || !staffId) return;
  
  try {
    const db = getFirestoreInstance();
    
    // Update the individual item
    const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', selectedItem.id);
    
    // Get current item data
    const itemDoc = await getDoc(itemRef);
    if (!itemDoc.exists()) return;
    
    const itemData = itemDoc.data();
    const assignedTourIds = itemData.assignedTourIds || {};
    
    // Check if tour exists in assigned tours
    if (!assignedTourIds[tourId]) return;
    
    // Remove staff ID from assigned staff array
    const updatedAssignedStaff = assignedTourIds[tourId].assignedStaff.filter(id => id !== staffId);
    
    // Update the item document
    await updateDoc(itemRef, {
      [`assignedTourIds.${tourId}.assignedStaff`]: updatedAssignedStaff,
      updatedAt: new Date().toISOString()
    });
    
    // Update local state
    const updatedItems = individualItems.map(item => {
      if (item.id === selectedItem.id) {
        const updatedItem = { ...item };
        if (updatedItem.assignedTourIds && updatedItem.assignedTourIds[tourId]) {
          updatedItem.assignedTourIds = {
            ...updatedItem.assignedTourIds,
            [tourId]: {
              ...updatedItem.assignedTourIds[tourId],
              assignedStaff: updatedAssignedStaff
            }
          };
        }
        return updatedItem;
      }
      return item;
    });
    
    setIndividualItems(updatedItems);
    setSelectedItem(updatedItems.find(item => item.id === selectedItem.id));
    
    alert('Staff removed successfully.');
  } catch (error) {
    console.error('Error removing staff:', error);
    alert('Failed to remove staff: ' + error.message);
  }
};

// Assign staff to item
const handleAssignStaff = async () => {
  if (!selectedItem || !userData?.userId || !selectedTourId || selectedStaffIds.length === 0) return;
  
  setIsUpdatingStaff(true);
  
  try {
    const db = getFirestoreInstance();
    
    // Update the individual item
    const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', selectedItem.id);
    
    // Get current item data
    const itemDoc = await getDoc(itemRef);
    if (!itemDoc.exists()) {
      setIsUpdatingStaff(false);
      return;
    }
    
    const itemData = itemDoc.data();
    const assignedTourIds = itemData.assignedTourIds || {};
    
    // Check if tour exists in assigned tours
    if (!assignedTourIds[selectedTourId]) {
      setIsUpdatingStaff(false);
      alert('This tour is no longer assigned to this item.');
      return;
    }
    
    // Get current assigned staff
    const currentAssignedStaff = assignedTourIds[selectedTourId].assignedStaff || [];
    
    // Merge current staff with newly selected staff (avoid duplicates)
    const updatedAssignedStaff = Array.from(new Set([...currentAssignedStaff, ...selectedStaffIds]));
    
    // Update the item document
    await updateDoc(itemRef, {
      [`assignedTourIds.${selectedTourId}.assignedStaff`]: updatedAssignedStaff,
      updatedAt: new Date().toISOString()
    });
    
    // Update local state
    const updatedItems = individualItems.map(item => {
      if (item.id === selectedItem.id) {
        const updatedItem = { ...item };
        if (updatedItem.assignedTourIds && updatedItem.assignedTourIds[selectedTourId]) {
          updatedItem.assignedTourIds = {
            ...updatedItem.assignedTourIds,
            [selectedTourId]: {
              ...updatedItem.assignedTourIds[selectedTourId],
              assignedStaff: updatedAssignedStaff
            }
          };
        }
        return updatedItem;
      }
      return item;
    });
    
    setIndividualItems(updatedItems);
    setSelectedItem(updatedItems.find(item => item.id === selectedItem.id));
    
    // Reset staff selection and close modal
    setSelectedStaffIds([]);
    setShowStaffModal(false);
    
    alert('Staff assigned successfully.');
  } catch (error) {
    console.error('Error assigning staff:', error);
    alert('Failed to assign staff: ' + error.message);
  } finally {
    setIsUpdatingStaff(false);
  }
};

// Handle staff selection for assignment
const handleStaffSelect = (staffId) => {
  const currentItem = individualItems.find(item => item.id === activeTab);
  const tourDetails = currentItem?.assignedTourIds?.[selectedTourId];
  
  // Check if staff is already assigned to this tour
  const isAlreadyAssigned = tourDetails?.assignedStaff?.includes(staffId);
  
  // If already assigned, don't allow selection
  if (isAlreadyAssigned) {
    alert('This staff member is already assigned to this item.');
    return;
  }
  
  setSelectedStaffIds(prev => {
    if (prev.includes(staffId)) {
      return prev.filter(id => id !== staffId);
    } else {
      return [...prev, staffId];
    }
  });
};

// Print QR code
const handlePrintQrCode = () => {
  const printContent = document.getElementById('qr-code-print-area');
  if (printContent) {
    // Create an iframe to print from (invisible)
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    // Write content to iframe
    iframe.contentDocument.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 20px;
            }
            .qr-container {
              display: inline-block;
              margin: 0 auto;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            h3 {
              margin-top: 5px;
              margin-bottom: 3px;
              font-size: 32px;
            }
            p {
              margin: 3px 0;
              font-size: 32px;
              color: #E2384D;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    iframe.contentDocument.close();
    
    // Focus and print
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    
    // Remove iframe after printing
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }
};

// Handle view file
const handleViewFile = (item) => {
  if (item.fileUrl) {
    setFileViewerUrl(item.fileUrl);
    setFileViewerName(item.name || getFileName(item.fileUrl));
    
    // Detect file type from URL extension
    const fileUrl = item.fileUrl.toLowerCase();
    if (fileUrl.endsWith('.pdf')) {
      setFileViewerType('application/pdf');
    } else if (fileUrl.match(/\.(jpe?g|png|gif|bmp|webp)$/)) {
      // Use the specific image type rather than generic 'image'
      const extension = fileUrl.split('.').pop();
      setFileViewerType(`image/${extension === 'jpg' ? 'jpeg' : extension}`);
    } else if (fileUrl.match(/\.(docx?)$/)) {
      setFileViewerType('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    } else if (fileUrl.match(/\.(xlsx?)$/)) {
      setFileViewerType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } else {
      setFileViewerType('application/octet-stream');
    }
    
    setShowFileViewer(true);
  }
};

// Extract filename from fileUrl if present
const getFileName = (url) => {
  if (!url) return 'Document';
  
  try {
    const parts = url.split('/');
    // Get the last part of the URL (filename)
    const rawFilename = parts[parts.length - 1];
    // Decode any URL encoded characters
    return decodeURIComponent(rawFilename);
  } catch (error) {
    console.error('Error extracting filename:', error);
    return 'Document';
  }
};

const handleRemoveDateAssignmentConfirm = (item, tourId, dateId) => {
  setItemToUnassign(item);
  setTourIdToUnassign(tourId);
  setDateIdToUnassign(dateId);
  setShowRemoveDateAssignmentConfirm(true);
};

// Format date for display
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

// Tab rendering function
const renderTabs = () => {
  return (
    <div className={styles.tabsContainer}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${activeTab === 'details' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Overview
        </button>
        
        {individualItems.map(item => (
          <button
            key={item.id}
            className={`${styles.tabButton} ${activeTab === item.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(item.id)}
          >
            {item.serialNumber || `Item ${item.id.substr(-4)}`}
          </button>
        ))}
      </div>
      
      <div className={styles.tabContent}>
        {activeTab === 'details' ? (
          renderOverviewTab()
        ) : (
          renderIndividualItemTab(individualItems.find(item => item.id === activeTab))
        )}
      </div>
    </div>
  );
};

// Overview tab content
const renderOverviewTab = () => {
  // Use groupData for overview
  if (!groupData) return null;
  
  return (
    <div className={styles.equipmentDetails}>
      <div className={styles.imageSection}>
        <div className={styles.imageContainer}>
          {groupData.coverPhotoUrl ? (
            <img 
              src={groupData.coverPhotoUrl} 
              alt={groupData.name} 
              className={styles.equipmentImage} 
            />
          ) : (
            <div className={styles.placeholderImage}>
              <span>{groupData.name.charAt(0)}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className={styles.infoSection}>
        <div className={styles.infoHeader}>
          <h1 className={styles.equipmentName}>{groupData.name}</h1>
          <div className={styles.categoryBadge}>
            {getCategoryDisplayName(category)}
          </div>
        </div>
        
        <div className={styles.basicInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Type:</span>
            <span className={styles.infoValue}>{groupData.type}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Sub Type:</span>
            <span className={styles.infoValue}>{groupData.subType}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Brand:</span>
            <span className={styles.infoValue}>{groupData.brand}</span>
          </div>
          {groupData.model && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Model:</span>
              <span className={styles.infoValue}>{groupData.model}</span>
            </div>
          )}
          
          <div className={styles.quantityInfo}>
            <span className={styles.quantityLabel}>Quantity</span>
            <span className={styles.quantityValue}>
              <span className={styles.totalQuantity}>{groupData.totalQuantity}</span>
              {groupData.availableQuantity < groupData.totalQuantity && (
                <span className={styles.availableQuantity}>
                  ({groupData.availableQuantity} available)
                </span>
              )}
            </span>
          </div>
          <div className={styles.priceInfo}>
            <span className={styles.priceLabel}>Rent Per Day</span>
            <span className={styles.priceValue}>₹{groupData.rentPerDay}</span>
          </div>
        </div>
        
        {/* Inventory status section - always show all statuses */}
        <div className={styles.inventoryStatusSection}>
          <h3 className={styles.sectionTitle}>Inventory Status</h3>
          <div className={styles.statusGrid}>
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Available</span>
              <span className={styles.statusValue}>{groupData.availableQuantity || 0}</span>
            </div>
            
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Reserved</span>
              <span className={styles.statusValue}>{groupData.reservedQuantity || 0}</span>
            </div>
            
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Maintenance</span>
              <span className={styles.statusValue}>{groupData.maintenanceQuantity || 0}</span>
            </div>
            
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Damaged</span>
              <span className={styles.statusValue}>{groupData.damagedQuantity || 0}</span>
            </div>
          </div>
        </div>
        
        {groupData.description && (
          <div className={styles.descriptionSection}>
            <h3 className={styles.sectionTitle}>Description</h3>
            <p className={styles.descriptionText}>{groupData.description}</p>
          </div>
        )}
        
        {groupData.fileUrl && (
          <div className={styles.attachmentSection}>
            <h3 className={styles.sectionTitle}>Attachment</h3>
            <button 
              className={styles.attachmentButton}
              onClick={() => handleViewFile(groupData)}
            >
              <FontAwesomeIcon 
                icon={groupData.fileUrl.toLowerCase().endsWith('.pdf') ? faFilePdf : faFileAlt} 
                className={styles.attachmentIcon}
              />
              <span className={styles.attachmentName}>
                {getFileName(groupData.fileUrl)}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// State for storing complete staff details
const [staffDetails, setStaffDetails] = useState({});

// Fetch staff details for all assigned staff
useEffect(() => {
  if (!userData?.userId) return;
  
  // Only fetch when we have selected an item with assigned staff
  if (activeTab === 'details' || !individualItems.length) return;
  
  const selectedItem = individualItems.find(item => item.id === activeTab);
  if (!selectedItem || !selectedItem.assignedTourIds) return;
  
  // Collect all unique staff IDs from all tours
  const staffIds = new Set();
  Object.values(selectedItem.assignedTourIds).forEach(tour => {
    if (tour.assignedStaff && Array.isArray(tour.assignedStaff)) {
      tour.assignedStaff.forEach(id => staffIds.add(id));
    }
  });
  
  // If we have staff IDs that haven't been fetched yet, fetch them
  const idsToFetch = Array.from(staffIds).filter(id => !staffDetails[id]);
  if (idsToFetch.length === 0) return;
  
  const fetchStaffDetails = async () => {
    const db = getFirestoreInstance();
    const newDetailsMap = { ...staffDetails };
    
    await Promise.all(idsToFetch.map(async (staffId) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', staffId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          newDetailsMap[staffId] = {
            id: staffId,
            fullName: userData.fullName || 'Unknown Staff',
            userName: userData.userName || '',
            profilePictureUrl: userData.profilePictureUrl || '',
            userSubType: userData.userSubType || '',
            email: userData.email || ''
          };
        }
      } catch (err) {
        console.error(`Error fetching staff details for ${staffId}:`, err);
      }
    }));
    
    setStaffDetails(newDetailsMap);
  };
  
  fetchStaffDetails();
}, [userData, activeTab, individualItems]);

// Individual item tab content
const renderIndividualItemTab = (item) => {
  if (!item) return <div className={styles.noItemData}>Item not found</div>;
  
  // Generate QR code URL
  const qrCodeUrl = item.qrCodeUrl || `${APP_URL}/scan/assigned-inventory/${item.id}`;
  
  return (
    <div className={styles.individualItemDetails}>
      <div className={styles.itemHeader}>
        <div className={styles.serialNumberBadge}>
          SN: {item.serialNumber}
        </div>
        <div className={styles.headerButtons}>
          <button
            className={styles.qrCodeButton}
            onClick={() => handleQrCodeClick(item)}
            title="View QR Code"
          >
            <FontAwesomeIcon icon={faQrcode} />
          </button>
          
          {/* Make status badge clickable only if allowed */}
          <div 
            className={`${styles.statusBadge} ${styles[`status${item.status}`]} ${!isActionAllowed('status') ? styles.disabledStatus : ''}`}
            onClick={() => isActionAllowed('status') ? handleStatusClick(item) : null}
            style={{ cursor: isActionAllowed('status') ? 'pointer' : 'default' }}
            title={isActionAllowed('status') ? "Click to change status" : "You don't have permission to change status"}
          >
            {item.status}
          </div>
        </div>
      </div>
      
      {/* Item photo and QR code side by side */}
      <div className={styles.itemPhotoAndQrContainer}>
        <div className={styles.imageSection}>
          <div className={styles.imageContainer}>
            {item.photoUrl ? (
              <img 
                src={item.photoUrl} 
                alt={`${item.name} - ${item.serialNumber}`} 
                className={styles.equipmentImage} 
              />
            ) : groupData?.coverPhotoUrl ? (
              <img 
                src={groupData.coverPhotoUrl} 
                alt={item.name} 
                className={styles.equipmentImage} 
              />
            ) : (
              <div className={styles.placeholderImage}>
                <span>{item.name.charAt(0)}</span>
              </div>
            )}
          </div>
        </div>

        {/* QR Code Section */}
        <div className={styles.qrCodeSection}>
          <h3 className={styles.sectionTitle}>Quick Identification</h3>
          <div className={styles.qrCodeContainer}>
            <div style={{ background: 'white', padding: '16px', margin: '0 auto' }}>
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "50%", width: "50%" }}
                value={qrCodeUrl}
                viewBox={`0 0 256 256`}
              />
            </div>
            <p className={styles.modelLabel}><strong>{item.model || 'No Model'}</strong></p>
            <p className={styles.qrCodeInstructions}>
              Scan for quick access
            </p>
            <button 
              className={styles.printQrButton}
              onClick={() => handleQrCodeClick(item)}
            >
              <FontAwesomeIcon icon={faPrint} className={styles.buttonIcon} />
              Print QR Code
            </button>
          </div>
        </div>
      </div>
      
      <div className={styles.itemDetailsGrid}>
        <div className={styles.detailsSection}>
          <h3 className={styles.sectionTitle}>Item Details</h3>
          
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Model</span>
              <span className={styles.detailValue}>{item.model || 'N/A'}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Condition</span>
              <span className={styles.detailValue}>{item.condition}</span>
            </div>
            
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Rent Per Day</span>
              <span className={styles.detailValue}>₹{item.rentPerDay}</span>
            </div>
            
            {item.yearOfPurchase && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Purchase Year</span>
                <span className={styles.detailValue}>{item.yearOfPurchase}</span>
              </div>
            )}
            
            {item.purchasePrice && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Purchase Price</span>
                <span className={styles.detailValue}>₹{item.purchasePrice}</span>
              </div>
            )}
            
            {item.warrantyExpiryDate && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Warranty Until</span>
                <span className={styles.detailValue}>
                  {new Date(item.warrantyExpiryDate).toLocaleDateString()}
                </span>
              </div>
            )}
            
            {item.location && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Location</span>
                <span className={styles.detailValue}>{item.location}</span>
              </div>
            )}
          </div>
        </div>
        
        {item.maintenanceNotes && (
          <div className={styles.notesSection}>
            <h3 className={styles.sectionTitle}>Maintenance Notes</h3>
            <p className={styles.notesText}>{item.maintenanceNotes}</p>
          </div>
        )}
        
        {/* Last Used Section - Always show */}
        <div className={styles.lastUsedSection}>
          <h3 className={styles.sectionTitle}>Last Used</h3>
          {item.lastUsed ? (
            <div className={styles.lastUsedInfo}>
              <div className={styles.lastUsedDate}>
                <span className={styles.lastUsedLabel}>Date:</span>
                <span className={styles.lastUsedValue}>
                  {new Date(item.lastUsed.date).toLocaleString()}
                </span>
              </div>
              
              {item.lastUsed.tourDateDocId && item.lastUsed.tourId && (
                <div className={styles.lastUsedTour}>
                  <span className={styles.lastUsedLabel}>Tour:</span>
                  <span className={styles.lastUsedValue}>
                    <a href={`/tour/${item.lastUsed.tourId}/${item.lastUsed.tourDateDocId}`} className={styles.tourLink}>
                      View Tour Details
                    </a>
                  </span>
                </div>
              )}
              
              {item.lastUsed.event && 
               item.lastUsed.event.venueDetails && 
               item.lastUsed.event.venueDetails.venueData && (
                <div className={styles.lastUsedLocation}>
                  <span className={styles.lastUsedLabel}>Venue:</span>
                  <span className={styles.lastUsedValue}>
                    {item.lastUsed.event.venueDetails.venueData.displayName || 'Unknown Venue'}
                  </span>
                </div>
              )}
              
              {item.lastUsed.event && 
               item.lastUsed.event.venueDetails && 
               item.lastUsed.event.venueDetails.venueData && (
                <div className={styles.lastUsedAddress}>
                  <span className={styles.lastUsedLabel}>Address:</span>
                  <span className={styles.lastUsedValue}>
                    {item.lastUsed.event.venueDetails.venueData.formattedAddress || 'No address available'}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noLastUsedInfo}>
              <p>This item has not been used at any venue yet.</p>
            </div>
          )}
        </div>
        
        {/* Current tour assignments section */}
        {item.assignedTourIds && Object.keys(item.assignedTourIds).length > 0 && (
          <div className={styles.assignmentsSection}>
            <h3 className={styles.sectionTitle}>Current Assignments</h3>
            <div className={styles.assignmentsList}>
              {Object.entries(item.assignedTourIds).map(([tourId, details]) => {
                return (
                  <div key={tourId} className={styles.assignmentItem}>
                    <div className={styles.assignmentHeader}>
                      <span className={styles.assignmentTourName}>
                        {details.tourName || 'Tour'} 
                        {details.actName && ` - ${details.actName}`}
                      </span>
                      <div className={styles.assignmentActions}>
                        {details.assignedDates && details.assignedDates.length > 0 && details.assignedDates[0].tourDateDocId && (
                          <a href={`/tour/${tourId}/${details.assignedDates[0].tourDateDocId}`} className={styles.viewTourButton}>
                            View Tour
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {details.actName && (
                      <div className={styles.assignmentDetail}>
                        <FontAwesomeIcon icon={faFileAlt} className={styles.assignmentIcon} />
                        <span>Act: {details.actName}</span>
                      </div>
                    )}
                    
                    {details.assignedDates && details.assignedDates.length > 0 && (
                      <div className={styles.assignedDates}>
                        <h4 className={styles.assignedDatesTitle}>
                          <FontAwesomeIcon icon={faCalendarAlt} className={styles.assignmentIcon} />
                          Assigned Dates:
                        </h4>
                        <div className={styles.datesList}>
                          {details.assignedDates.map((dateInfo, index) => (
                            <div key={index} className={styles.dateItem}>
                              <div className={styles.dateItemContent}>
                                <span className={styles.dateItemDate}>
                                  {new Date(dateInfo.date).toLocaleDateString()}
                                </span>
                                {dateInfo.event && 
                                 dateInfo.event.venueDetails && 
                                 dateInfo.event.venueDetails.venueData && (
                                  <span className={styles.dateItemVenue}>
                                    {` - ${dateInfo.event.venueDetails.venueData.displayName}`}
                                  </span>
                                )}
                              </div>
                              {dateInfo.tourDateDocId && (
                                <button
                                  className={styles.removeDateButton}
                                  onClick={() => handleRemoveDateAssignmentConfirm(item, tourId, dateInfo.tourDateDocId)}
                                  title="Remove from this date"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Venue Information - Now using event path */}
                        {details.assignedDates && details.assignedDates.length > 0 && 
                         details.assignedDates[0].event && 
                         details.assignedDates[0].event.venueDetails && 
                         details.assignedDates[0].event.venueDetails.venueData && (
                          <div className={styles.venueInfoSection}>
                            <div className={styles.venueNameDetail}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.assignmentIcon} />
                              <span>Venue: {details.assignedDates[0].event.venueDetails.venueData.displayName || 'Unknown Venue'}</span>
                            </div>
                            <div className={styles.venueAddressDetail}>
                              <span className={styles.venueAddress}>
                                {details.assignedDates[0].event.venueDetails.venueData.formattedAddress || 'No address available'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Assigned Staff Section */}
                    <div className={styles.assignedStaffSection}>
                      <h4 className={styles.assignedStaffTitle}>
                        <FontAwesomeIcon icon={faUser} className={styles.assignmentIcon} />
                        Assigned Staff:
                      </h4>
                      
                      {details.assignedStaff && details.assignedStaff.length > 0 ? (
                        <div className={styles.staffList}>
                          {details.assignedStaff.map((staffId) => {
                            const staff = staffDetails[staffId] || { id: staffId };
                            return (
                              <div key={staffId} className={styles.staffItem}>
                                <div className={styles.staffItemContent}>
                                  <div className={styles.staffImageContainer}>
                                    {staff.profilePictureUrl ? (
                                      <img 
                                        src={staff.profilePictureUrl} 
                                        alt={staff.fullName} 
                                        className={styles.staffImage}
                                      />
                                    ) : (
                                      <div className={styles.defaultStaffImage}>
                                        <FontAwesomeIcon icon={faUser} />
                                      </div>
                                    )}
                                  </div>
                                  <div className={styles.staffInfo}>
                                    <div className={styles.staffName}>
                                      {staff.fullName || `Staff (${staffId.substring(0, 6)}...)`}
                                    </div>
                                    {staff.userName && (
                                      <div className={styles.staffUsername}>
                                        @{staff.userName} {staff.userSubType && `• ${staff.userSubType}`}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button 
                                  className={styles.removeStaffButton}
                                  onClick={() => handleRemoveStaff(tourId, staffId)}
                                  title="Remove staff from assignment"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className={styles.noStaffMessage}>
                          No staff assigned to this tour.
                        </div>
                      )}
                      
                      <button 
                        className={styles.assignStaffButton}
                        onClick={() => handleManageStaffClick(tourId)}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                        <span>Assign Staff</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Status Modal
const renderStatusModal = () => {
  if (!selectedItem) return null;

  return (
    <div className={styles.modalOverlay} onClick={() => setShowStatusModal(false)}>
      <div className={styles.statusModal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.statusModalTitle}>
          {selectedItem.status === 'Reserved' ? 'Item Status Information' : 'Update Item Status'}
        </h3>
        
        <div className={styles.statusModalContent}>
          {selectedItem.status === 'Reserved' && selectedItem.assignedTourIds ? (
            <div className={styles.tourAssignmentInfo}>
              <p className={styles.statusModalText}>
                This item is currently reserved for the following tour(s):
              </p>
              <div className={styles.tourAssignmentList}>
                {Object.entries(selectedItem.assignedTourIds).map(([tourId, details]) => (
                  <div key={tourId} className={styles.tourAssignmentItem}>
                 
                    
                    {details.actName && (
                      <div className={styles.assignmentDetail}>
                        <FontAwesomeIcon icon={faFileAlt} className={styles.assignmentIcon} />
                        <span>Act: {details.actName}</span>
                      </div>
                    )}
                    
                   
                    
                    {details.assignedDates && details.assignedDates.length > 0 && (
                      <div className={styles.tourDatesList}>
                        <h5 className={styles.tourDatesTitle}>
                          <FontAwesomeIcon icon={faCalendarAlt} className={styles.assignmentIcon} />
                          Dates:
                        </h5>
                        <ul className={styles.dateItems}>
                          {details.assignedDates.map((dateInfo, index) => (
                            <li key={index} className={styles.dateItem}>
                              {new Date(dateInfo.date).toLocaleDateString()}
                              {dateInfo.event && 
                               dateInfo.event.venueDetails && 
                               dateInfo.event.venueDetails.venueData && 
                               ` - ${dateInfo.event.venueDetails.venueData.displayName}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                     {/* Venue information */}
                     {details.assignedDates && details.assignedDates.length > 0 && 
                     details.assignedDates[0].event && 
                     details.assignedDates[0].event.venueDetails && 
                     details.assignedDates[0].event.venueDetails.venueData && (
                      <div className={styles.venueDetail}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.assignmentIcon} />
                        <span>Venue: {details.assignedDates[0].event.venueDetails.venueData.displayName}</span>
                        <div className={styles.venueAddress}>
                          {details.assignedDates[0].event.venueDetails.venueData.formattedAddress}
                        </div>
                      </div>
                    )}
                    
                    {details.assignedDates && details.assignedDates.length > 0 && details.assignedDates[0].tourDateDocId && (
                      <a href={`/tour/${tourId}/${details.assignedDates[0].tourDateDocId}`} className={styles.viewTourLink}>
                        View Tour Details
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : selectedItem.status !== 'Reserved' ? (
            <>
              <p className={styles.statusModalText}>
                Select a new status for this item:
              </p>
              <div className={styles.statusButtons}>
                {selectedItem.status !== 'Available' && (
                  <button
                    className={`${styles.statusButton} ${styles.availableButton}`}
                    onClick={() => handleUpdateStatus('Available')}
                    disabled={isUpdatingStatus}
                  >
                    <FontAwesomeIcon icon={faCheck} className={styles.statusButtonIcon} />
                    <span>Available</span>
                  </button>
                )}
                
                {selectedItem.status !== 'Maintenance' && (
                  <button
                    className={`${styles.statusButton} ${styles.maintenanceButton}`}
                    onClick={() => handleUpdateStatus('Maintenance')}
                    disabled={isUpdatingStatus}
                  >
                    <FontAwesomeIcon icon={faTools} className={styles.statusButtonIcon} />
                    <span>Maintenance</span>
                  </button>
                )}
                
                {selectedItem.status !== 'Damaged' && (
                  <button
                    className={`${styles.statusButton} ${styles.damagedButton}`}
                    onClick={() => handleUpdateStatus('Damaged')}
                    disabled={isUpdatingStatus}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className={styles.statusButtonIcon} />
                    <span>Damaged</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className={styles.statusModalText}>
              Status cannot be changed while item is in <strong>{selectedItem.status}</strong> status.
              Reserved items are currently assigned to a tour and cannot be modified.
            </p>
          )}
        </div>
        
        <div className={styles.statusModalActions}>
          <button 
            className={styles.closeButton} 
            onClick={() => setShowStatusModal(false)}
            disabled={isUpdatingStatus}
          >
            Close
          </button>
        </div>
        
        {isUpdatingStatus && (
          <div className={styles.updatingOverlay}>
            <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
            <span>Updating status...</span>
          </div>
        )}
      </div>
    </div>
  );
};

// QR Code Modal
const renderQrCodeModal = () => {
  if (!selectedQrItem) return null;

  // Generate QR code URL
  const qrCodeUrl = selectedQrItem.qrCodeUrl || `${APP_URL}/scan/assigned-inventory/${selectedQrItem.id}`;

  return (
    <div className={styles.modalOverlay} onClick={() => setShowQrModal(false)}>
      <div className={styles.qrCodeModal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.qrCodeModalTitle}>
          QR Code for {selectedQrItem.serialNumber}
        </h3>
        
        <div className={styles.qrCodeModalContent} id="qr-code-print-area">
          <div className={styles.qrCodeContainer}>
            <div style={{ background: 'white', padding: '16px', margin: '0 auto' }}>
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={qrCodeUrl}
                viewBox={`0 0 256 256`}
              />
            </div>
            <h3>{selectedQrItem.name}</h3>
            <p><strong>Model: {selectedQrItem.model || 'N/A'}</strong></p>
            <p>{selectedQrItem.serialNumber}</p>
            <p>{selectedQrItem.brand}</p>
          </div>
        </div>
        
        <div className={styles.qrCodeModalActions}>
          <button 
            className={styles.printButton} 
            onClick={handlePrintQrCode}
          >
            <FontAwesomeIcon icon={faPrint} className={styles.buttonIcon} />
            Print QR Code
          </button>
          <button 
            className={styles.closeButton} 
            onClick={() => setShowQrModal(false)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Staff Assignment Modal
const renderStaffModal = () => {
  if (!selectedTourId) return null;
  
  // Get already assigned staff for this tour
  const currentItem = individualItems.find(item => item.id === activeTab);
  const assignedStaffIds = currentItem?.assignedTourIds?.[selectedTourId]?.assignedStaff || [];

  return (
    <div className={styles.modalOverlay} onClick={() => setShowStaffModal(false)}>
      <div className={styles.staffModal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.staffModalTitle}>
          Assign Staff to Equipment
        </h3>
        
        <div className={styles.staffModalContent}>
          {isLoadingStaff ? (
            <div className={styles.loadingContainer}>
              <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
              <p>Loading staff members...</p>
            </div>
          ) : availableStaff.length === 0 ? (
            <div className={styles.noStaffMessage}>
              <p>No staff members available. Add staff in Staff Management.</p>
            </div>
          ) : (
            <>
              <p className={styles.staffModalText}>
                Select staff members to assign to this equipment:
              </p>
              <div className={styles.staffSelectionList}>
                {availableStaff.map(staff => {
                  // Check if this staff is already assigned
                  const isAlreadyAssigned = assignedStaffIds.includes(staff.id);
                  
                  return (
                    <div 
                      key={staff.id}
                      className={`${styles.staffSelectionItem} 
                                 ${selectedStaffIds.includes(staff.id) ? styles.selectedStaffItem : ''} 
                                 ${isAlreadyAssigned ? styles.disabledStaffItem : ''}`}
                      onClick={() => !isAlreadyAssigned && handleStaffSelect(staff.id)}
                    >
                      <div className={styles.staffImageContainer}>
                        {staff.profilePictureUrl ? (
                          <img 
                            src={staff.profilePictureUrl} 
                            alt={staff.fullName} 
                            className={styles.staffImage}
                          />
                        ) : (
                          <div className={styles.defaultStaffImage}>
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                        )}
                      </div>
                      <div className={styles.staffInfo}>
                        <h4 className={styles.staffName}>{staff.fullName}</h4>
                        <p className={styles.staffDetails}>
                          @{staff.userName} • {staff.userSubType}
                        </p>
                      </div>
                      {isAlreadyAssigned ? (
                        <div className={styles.alreadyAssignedIcon}>
                          <FontAwesomeIcon icon={faCheck} />
                          <span>Already Assigned</span>
                        </div>
                      ) : selectedStaffIds.includes(staff.id) && (
                        <div className={styles.checkIcon}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        
        <div className={styles.staffModalActions}>
          <button 
            className={styles.cancelButton} 
            onClick={() => setShowStaffModal(false)}
            disabled={isUpdatingStaff}
          >
            Cancel
          </button>
          <button 
            className={styles.assignButton} 
            onClick={handleAssignStaff}
            disabled={isUpdatingStaff || selectedStaffIds.length === 0}
          >
            {isUpdatingStaff ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Assigning...</span>
              </>
            ) : (
              <span>Assign Staff</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Content to display based on state
const renderContent = () => {
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
        <p>Loading equipment details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FontAwesomeIcon icon={faFileAlt} className={styles.errorIcon} />
        <h2>Error</h2>
        <p>{error}</p>
        <button className={styles.backButton} onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Inventory</span>
        </button>
      </div>
    );
  }
  
  if (!groupData) {
    return (
      <div className={styles.notFoundContainer}>
        <FontAwesomeIcon icon={faFileAlt} className={styles.notFoundIcon} />
        <h2>Item Not Found</h2>
        <p>The requested equipment item could not be found.</p>
        <button className={styles.backButton} onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Inventory</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.detailsContainer}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Inventory</span>
        </button>
        <div className={styles.actionButtons}>
          {/* Only show edit button if allowed */}
          {isActionAllowed('edit') && (
            <button className={styles.editButton} onClick={handleEdit}>
              <FontAwesomeIcon icon={faEdit} />
              <span>Edit</span>
            </button>
          )}
          
          {/* Only show delete button if allowed */}
          {isActionAllowed('delete') && (
            <button className={styles.deleteButton} onClick={handleDeleteConfirm}>
              <FontAwesomeIcon icon={faTrash} />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Render overview and individual item tabs */}
      {renderTabs()}
    </div>
  );
};

return (
  <div className={styles.inventoryContainer}>
    <div className={styles['main-layout']}>
      {!isMobile && <LeftMenu />}
      <main className={styles['equipment-details-main']}>
        {renderContent()}
        
        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Equipment"
          message="Are you sure you want to delete this equipment? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="dangerButton"
        />


        
        {/* Status Modal */}
        {showStatusModal && renderStatusModal()}
        
        {/* QR Code Modal */}
        {showQrModal && renderQrCodeModal()}
        
        {/* Staff Assignment Modal */}
        {showStaffModal && renderStaffModal()}
        
      </main>
      {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
    </div>

    <ConfirmModal
  isOpen={showRemoveDateAssignmentConfirm}
  onClose={() => setShowRemoveDateAssignmentConfirm(false)}
  onConfirm={handleRemoveDateAssignment}
  title="Remove Date Assignment"
  message={`Are you sure you want to remove this item from this specific date? This will make the item available for other assignments.`}
  confirmText="Remove"
  cancelText="Cancel"
  confirmButtonClass="dangerButton"
/>
    
    {/* File Viewer Modal */}
    {groupData && (
      <FileViewerModal
        isOpen={showFileViewer}
        onClose={() => setShowFileViewer(false)}
        file={{
          fileUrl: fileViewerUrl,
          fileType: fileViewerType,
          fileName: fileViewerName || 'Document'
        }}
      />
    )}
  </div>
);
};

export default EquipmentCardDetails;